import camelcaseKeys from 'camelcase-keys'
import { create } from 'zustand'

import { executeDelete, executeGet, executePost } from '@/services/lukita'
import type {
  CreateFilingBody,
  Filing,
  FilingDetail,
  FilingDetailDTO,
  FilingDTO,
} from './types'

const BASE = '/tools/bankruptcy/filings'

interface FilingsStoreActions {
  reset: () => void
  setFilings: (filings: FilingDTO[]) => void
  setCurrentFilingDetail: (id: number | null) => void
  upsertFiling: (filing: FilingDTO) => void
  setFilingDetail: (detail: FilingDetailDTO | FilingDetail) => void
}

interface FilingsStoreLoaders {
  fetchFilings: () => Promise<void>
  refreshFilings: () => Promise<void>
  fetchFilingDetail: (id: number) => Promise<void>
}

interface FilingsStoreMutations {
  createFiling: (body: CreateFilingBody) => Promise<Filing>
  attachDocuments: (id: number, blobsIds: string[]) => Promise<Filing>
  screenFiling: (id: number) => Promise<Filing>
  convertFilingToProject: (id: number) => Promise<Filing>
  removeDocument: (filingId: number, documentId: number) => Promise<void>
}

// A case's own fetch, apart from the list: "missing" only after the server says so.
export type FilingDetailStatus = 'loading' | 'loaded' | 'missing' | 'failed'

interface FilingsStore {
  detailStatusById: Record<number, FilingDetailStatus>
  loadingFilings: boolean
  mutating: boolean
  filings: Filing[]
  filingDetail: FilingDetail | null
  detailsById: Record<number, FilingDetail>
  actions: FilingsStoreActions
  loaders: FilingsStoreLoaders
  mutations: FilingsStoreMutations
}

const atPageLimit = (filing: Filing) => filing.numberOfPages >= filing.pageLimit

export const useFilingsStore = create<FilingsStore>((set, get, store) => ({
  detailStatusById: {},
  loadingFilings: true,
  mutating: false,
  filingDetail: null,
  filings: [],
  detailsById: {},
  actions: {
    reset: () => set(store.getInitialState()),
    setCurrentFilingDetail: (id) => {
      const filingDetail = id ? get().detailsById[id] : null
      set({ filingDetail })
    },
    setFilings: (filings) =>
      set({
        filings: (camelcaseKeys(filings) as Filing[]).map((filing) => ({
          ...filing,
          atPageLimit: atPageLimit(filing),
        })),
        loadingFilings: false,
      }),
    upsertFiling: (dto) => {
      const filing = camelcaseKeys(dto) as Filing
      filing.atPageLimit = atPageLimit(filing)
      const detail = get().detailsById[filing.id] || {}

      get().actions.setFilingDetail({ ...detail, ...filing })

      set((state) => {
        const rest = state.filings.filter((row) => row.id !== filing.id)
        return { filings: [filing, ...rest] }
      })
    },
    setFilingDetail: (dto) => {
      const detail = camelcaseKeys(dto, { deep: true }) as FilingDetail
      detail.atPageLimit = detail.numberOfPages >= detail.pageLimit
      detail.documentsById = new Map(
        detail.documents?.map((document) => [
          document.id,
          {
            ...document,
            pageBboxesByNumber: new Map(
              document.pageBboxes?.map((item) => [item.page, item]),
            ),
          },
        ]),
      )

      set((state) => ({
        detailsById: { ...state.detailsById, [detail.id]: detail },
        detailStatusById: { ...state.detailStatusById, [detail.id]: 'loaded' },
      }))
    },
  },

  loaders: {
    fetchFilings: async () => {
      set({ loadingFilings: true })
      await get().loaders.refreshFilings()
    },

    refreshFilings: async () => {
      const response = await executeGet(BASE)
      get().actions.setFilings(await response.json())
    },

    fetchFilingDetail: async (id) => {
      const setStatus = (status: FilingDetailStatus) =>
        set((state) => ({
          detailStatusById: { ...state.detailStatusById, [id]: status },
        }))
      if (!get().detailsById[id]) setStatus('loading')

      try {
        const response = await executeGet(`${BASE}/${id}`)
        get().actions.setFilingDetail(await response.json())
        get().actions.setCurrentFilingDetail(null)
      } catch (error) {
        if (get().detailsById[id]) return
        setStatus(
          (error as { status?: number }).status === 404 ? 'missing' : 'failed',
        )
      }
    },
  },

  mutations: {
    createFiling: async (body) => {
      set({ mutating: true })
      const response = await executePost(BASE, { filing: body }).finally(() =>
        set({ mutating: false }),
      )
      const dto = (await response.json()) as FilingDTO
      get().actions.upsertFiling(dto)

      return camelcaseKeys(dto) as Filing
    },

    attachDocuments: async (id, blobsIds) => {
      set({ mutating: true })
      const response = await executePost(`${BASE}/${id}/documents`, {
        blobs_ids: blobsIds,
      }).finally(() => set({ mutating: false }))

      const dto = (await response.json()) as FilingDTO
      get().actions.upsertFiling(dto)

      return camelcaseKeys(dto) as Filing
    },

    screenFiling: async (id) => {
      set({ mutating: true })
      const response = await executePost(`${BASE}/${id}/screen`, {}).finally(
        () => set({ mutating: false }),
      )

      const dto = (await response.json()) as FilingDTO
      get().actions.upsertFiling(dto)

      return camelcaseKeys(dto) as Filing
    },

    convertFilingToProject: async (id) => {
      set({ mutating: true })
      const response = await executePost(`${BASE}/${id}/convert`, {}).finally(
        () => set({ mutating: false }),
      )

      const dto = (await response.json()) as FilingDTO
      get().actions.upsertFiling(dto)

      return camelcaseKeys(dto) as Filing
    },

    removeDocument: async (filingId, documentId) => {
      set({ mutating: true })
      const response = await executeDelete(
        `${BASE}/${filingId}/documents/${documentId}`,
      ).finally(() => set({ mutating: false }))

      const dto = (await response.json()) as FilingDTO
      get().actions.upsertFiling(dto)
    },
  },
}))
